/**
 * The SentinelOne credential form (Story 3): console URL plus a read-only
 * API token, validated by the API against the console before it is
 * stored. The token is write-only — this form never shows a stored value,
 * and the same form is "Add" and "Rotate". Lifted from the Clients page
 * to the Integrations pages (task 69).
 */

import { useState } from 'react';

import type { ClientTenant } from '@bluesphere/api-client';
import { PillButton } from '@bluesphere/ui-kit';

import { ConnectErrorNote, ErrorNote, INPUT, ModalShell } from '@bluesphere/mdr-alert-panel';

export function SentinelOneModal({
  tenant,
  onCancel,
  onSubmit,
  isPending,
  error,
  canAddService = false,
  onAddService = () => {},
}: {
  tenant: ClientTenant;
  onCancel: () => void;
  onSubmit: (consoleUrl: string, apiToken: string) => void;
  isPending: boolean;
  error: unknown;
  /** A platform Admin may add the covering service on 409 `service_required` (task 179c). */
  canAddService?: boolean;
  onAddService?: () => void;
}) {
  const [consoleUrl, setConsoleUrl] = useState('');
  const [apiToken, setApiToken] = useState('');
  const url = consoleUrl.trim();
  const canSubmit = url.startsWith('https://') && apiToken.trim().length > 0 && !isPending;

  return (
    <ModalShell onClose={onCancel} width="max-w-lg">
      <div className="border-b border-bluesphere-border px-6 py-5">
        <h2 className="text-lg font-semibold text-bluesphere-textOnLight">
          SentinelOne for {tenant.display_name}
        </h2>
        <p className="mt-1 text-sm text-bluesphere-textSecondary">
          A read-only API token from a Service User scoped to the Client's site. It is checked
          against the console before it is saved, stored under the Client's own secret path, and
          never shown again. Polling starts on the next tick with a 30-day back-fill.
        </p>
      </div>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (canSubmit) onSubmit(url, apiToken.trim());
        }}
        className="space-y-4 px-6 py-5"
        autoComplete="off"
      >
        <label className="block">
          <span className="text-xs uppercase tracking-wider text-bluesphere-textSecondary">
            Console URL
          </span>
          <input
            type="url"
            autoFocus
            value={consoleUrl}
            onChange={(e) => setConsoleUrl(e.target.value)}
            placeholder="https://<region>-<partner>.sentinelone.net"
            className={INPUT}
          />
        </label>
        <label className="block">
          <span className="text-xs uppercase tracking-wider text-bluesphere-textSecondary">
            API token
          </span>
          <input
            type="password"
            value={apiToken}
            onChange={(e) => setApiToken(e.target.value)}
            autoComplete="new-password"
            className={INPUT}
          />
          <span className="mt-1 block text-xs text-bluesphere-muted">
            Viewer role, scoped to the site the Client's agents are in. A token scoped to a site
            with no agents validates but never returns a threat.
          </span>
        </label>
        <ConnectErrorNote
          error={error}
          canAddService={canAddService}
          onAddService={onAddService}
          isPending={isPending}
        />
        <div className="flex items-center justify-end gap-2 pt-2">
          <PillButton variant="ghost" size="sm" type="button" onClick={onCancel}>
            Cancel
          </PillButton>
          <PillButton size="sm" type="submit" disabled={!canSubmit}>
            {isPending ? 'Validating…' : 'Validate and save'}
          </PillButton>
        </div>
      </form>
    </ModalShell>
  );
}
